import type { ProviderSummary } from './wizard';
import type { ProviderOption } from './chat';

/** Normalized provider data for shared card and detail modal components. */
export interface ProviderCardItem {
  id: string;
  providerId: string;
  name: string;
  city: string | null;
  district: string | null;
  rating: number | null;
  image: string | null;
  services: ServiceDetail[];
  serviceCount: number;
  priceDisplay: string;
  capacity: number | null;
  description?: string;
}

export interface ServiceDetail {
  name: string;
  description?: string;
  specs?: { name: string; value: string }[];
  pricing?: { price: number; currency: string; unit: string; description: string }[];
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  PEN: 'S/.',
  USD: '$',
  EUR: '\u20AC',
  MXN: 'MX$',
  COP: 'COL$',
  CLP: 'CLP$',
  ARS: 'AR$',
};

function formatPriceRange(range: ProviderSummary['price_range']): string {
  const { min, max, currency } = range;
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  if (min !== null && max !== null) {
    if (min === max) return `${symbol}${min.toLocaleString()}`;
    return `${symbol}${min.toLocaleString()} - ${symbol}${max.toLocaleString()}`;
  }
  if (min !== null) return `From ${symbol}${min.toLocaleString()}`;
  if (max !== null) return `Up to ${symbol}${max.toLocaleString()}`;
  return 'Price on request';
}

export function fromProviderSummary(p: ProviderSummary): ProviderCardItem {
  return {
    id: p.id,
    providerId: p.id,
    name: p.nombre_comercial,
    city: p.ciudad,
    district: p.distrito,
    rating: p.calificacion,
    image: p.image,
    services: p.services.map((name) => ({ name })),
    serviceCount: p.service_count,
    priceDisplay: formatPriceRange(p.price_range),
    capacity: p.capacity,
  };
}

export function fromProviderOption(opt: ProviderOption): ProviderCardItem {
  const pricingDetails = opt.pricing?.details ?? [];
  const service: ServiceDetail = {
    name: opt.service_name,
    description: opt.description,
    specs: opt.specs,
    pricing: pricingDetails,
  };

  const priceDisplay =
    !opt.pricing?.min_price || opt.pricing.min_price === 0 ? 'A consultar' : opt.pricing.display;

  return {
    id: opt.id,
    providerId: opt.provider_id,
    name: opt.name,
    city: opt.location?.city ?? null,
    district: opt.location?.district ?? null,
    rating: opt.rating,
    image: opt.image_url ?? null,
    services: [service],
    serviceCount: 1,
    priceDisplay,
    capacity: opt.capacity,
    description: opt.description,
  };
}
